import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";

// Cache del logo + info empresa (una sola llamada por sesión del navegador)
let _companyCache = null;
async function _loadCompany() {
  if (_companyCache) return _companyCache;
  try {
    const [logoR, infoR] = await Promise.allSettled([
      api.get("/company/logo"),
      api.get("/company/info"),
    ]);
    _companyCache = {
      logo: logoR.status === "fulfilled" ? (logoR.value?.data?.data_url || null) : null,
      info: infoR.status === "fulfilled" ? (infoR.value?.data || {}) : {},
    };
  } catch (e) {
    _companyCache = { logo: null, info: {} };
  }
  return _companyCache;
}

function _drawHeader(doc, { title, subtitle, company }) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  const logo = company?.logo;

  // Logo arriba izquierda
  if (logo) {
    try { doc.addImage(logo, "PNG", marginX, 8, 36, 16); }
    catch (e) { /* ignore */ }
  }

  // Datos empresa arriba derecha (alineados a la derecha)
  const info = company?.info || {};
  doc.setFontSize(9);
  doc.setTextColor("#4a5c53");
  const rx = pageWidth - marginX;
  const lines = [
    info.razon_social || "",
    info.ruc ? `RUC ${info.ruc}` : "",
    info.email_contacto || "",
    info.telefono || "",
  ].filter(Boolean);
  lines.forEach((l, i) => doc.text(l, rx, 12 + i * 4, { align: "right" }));

  // Título del documento
  doc.setFontSize(16);
  doc.setTextColor("#15241d");
  doc.text(title || "Reporte", marginX, 34);
  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor("#5c7368");
    doc.text(subtitle, marginX, 40);
  }
  // Fecha de generación
  doc.setFontSize(9);
  doc.setTextColor("#8ba397");
  doc.text(new Date().toLocaleString("es-EC"), pageWidth - marginX, 40, { align: "right" });

  // Línea separadora
  doc.setDrawColor(178, 208, 194);
  doc.setLineWidth(0.3);
  doc.line(marginX, 44, pageWidth - marginX, 44);
}

export async function exportToPDF({ title = "Reporte", subtitle, columns = [], rows = [], filename = "reporte.pdf", orientation = "landscape" }) {
  const doc = new jsPDF({ orientation });
  const company = await _loadCompany();
  _drawHeader(doc, { title, subtitle, company });

  autoTable(doc, {
    head: [columns.map(c => c.label)],
    body: rows.map(row => columns.map(c => {
      const val = typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor];
      return val == null ? "" : String(val);
    })),
    startY: 50,
    theme: "grid",
    headStyles: { fillColor: [32, 92, 69], textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 8, cellPadding: 2 },
    alternateRowStyles: { fillColor: [247, 249, 248] },
    didDrawPage: (data) => {
      // Footer con dirección + número de página
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor("#8ba397");
      const dir = company?.info?.direccion || "";
      if (dir) doc.text(dir, pageWidth / 2, pageHeight - 8, { align: "center" });
      doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - 14, pageHeight - 8, { align: "right" });
    },
  });
  doc.save(filename);
}

export function exportToExcel({ columns = [], rows = [], filename = "reporte.xlsx", sheetName = "Reporte" }) {
  const data = [
    columns.map(c => c.label),
    ...rows.map(row => columns.map(c => {
      const val = typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor];
      return val == null ? "" : val;
    })),
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}
